import { MinusCircleOutlined, PlusOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import { Button, Col, DatePicker, Form, InputNumber, Row, Tooltip } from 'antd'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { Editor } from '@/components'
import { UserInfo } from '@/modules/user'
import { removeHtmlTag } from '@/utils/remove-html-tag'

import { Production, ProductionFlowStatus, ProductionType } from '../production.schema'
import { ProductionFormInputMaxCount } from './util'

export const ProductionTargetForm: React.FC<{
  production?: Production
  userInfo: UserInfo
  projectType?: ProductionType
}> = ({ production, userInfo, projectType }) => {
  const { t } = useTranslation()

  const StatusBetweenResourceEstimateAndDevOnGoing = [
    ProductionFlowStatus.待立项申请,
    ProductionFlowStatus.待指派TO,
    ProductionFlowStatus.待资源预估,
    ProductionFlowStatus.待立项审批,
    ProductionFlowStatus.待IPD审批,
  ]
  const canEditStage = !production || StatusBetweenResourceEstimateAndDevOnGoing.includes(production?.flowStatus)

  const canEditPredictWorkload =
    !!production && [ProductionFlowStatus.待立项审批, ProductionFlowStatus.待IPD审批].includes(production?.flowStatus)

  const canAddStage = userInfo.isSuperAdmin || canEditStage
  const canDeleteStage = userInfo.isSuperAdmin || canEditStage

  return projectType ? (
    <>
      <div className='mb-4'>
        <div className='text-base font-bold'>{t('production.detail.projectTarget')}</div>
        <div className='px-2 flex flex-col'>
          <span>{t(`production.form.productionType.${ProductionType[projectType]}`)}</span>
          <span className='text-gray-500'>{t(`production.detail.typeDescription_${projectType}`)}</span>

          <Row>
            <Col span={24}>
              <Form.Item
                valuePropName='content'
                label={
                  <>
                    <span>{t('production.detail.businessResult')}</span>
                    <Tooltip title={t('production.detail.businessResultDescription')}>
                      <QuestionCircleOutlined className='ml-4px text-gray-500' />
                    </Tooltip>
                  </>
                }
                name='businessResult'
                trigger='onChange'
                rules={[
                  {
                    required: true,
                    validator: async (_, value) =>
                      !removeHtmlTag(value).trim()
                        ? Promise.reject(`${t('production.detail.businessResult')}是必选填项!`)
                        : Promise.resolve(),
                  },
                ]}
              >
                <Editor
                  textMaxCount={ProductionFormInputMaxCount}
                  placeholder={t('production.detail.businessResultDescription')}
                  className={'editorBorder'}
                  disableAutoSaveStyle
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                valuePropName='content'
                label={
                  <>
                    <span>{t('production.detail.userAcceptance')}</span>
                    <Tooltip title={t('production.detail.userAcceptanceDescription')}>
                      <QuestionCircleOutlined className='ml-4px text-gray-500' />
                    </Tooltip>
                  </>
                }
                name='userAcceptance'
                trigger='onChange'
                rules={[
                  {
                    required: true,
                    validator: async (_, value) =>
                      !removeHtmlTag(value).trim()
                        ? Promise.reject(`${t('production.detail.userAcceptance')}是必选填项!`)
                        : Promise.resolve(),
                  },
                ]}
              >
                <Editor
                  textMaxCount={ProductionFormInputMaxCount}
                  placeholder={t('production.detail.userAcceptanceDescription')}
                  className={'editorBorder'}
                  disableAutoSaveStyle
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                valuePropName='content'
                label={
                  <>
                    <span>{t('production.detail.productQuality')}</span>
                    <Tooltip title={t('production.detail.productQualityDescription')}>
                      <QuestionCircleOutlined className='ml-4px text-gray-500' />
                    </Tooltip>
                  </>
                }
                name='productQuality'
                trigger='onChange'
                rules={[
                  {
                    required: true,
                    validator: async (_, value) =>
                      !removeHtmlTag(value).trim()
                        ? Promise.reject(`${t('production.detail.productQuality')}是必选填项!`)
                        : Promise.resolve(),
                  },
                ]}
              >
                <Editor
                  textMaxCount={ProductionFormInputMaxCount}
                  placeholder={t('production.detail.productQualityDescription')}
                  className={'editorBorder'}
                  disableAutoSaveStyle
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                valuePropName='content'
                label={
                  <>
                    <span>{t('production.detail.marketIndicators')}</span>
                    <Tooltip title={t('production.detail.marketIndicatorsDescription')}>
                      <QuestionCircleOutlined className='ml-4px text-gray-500' />
                    </Tooltip>
                  </>
                }
                name='marketIndicators'
                trigger='onChange'
                rules={[
                  {
                    required: true,
                    validator: async (_, value) =>
                      !removeHtmlTag(value).trim()
                        ? Promise.reject(`${t('production.detail.marketIndicators')}是必选填项!`)
                        : Promise.resolve(),
                  },
                ]}
              >
                <Editor
                  textMaxCount={ProductionFormInputMaxCount}
                  placeholder={t('production.detail.marketIndicatorsDescription')}
                  className={'editorBorder'}
                  disableAutoSaveStyle
                />
              </Form.Item>
            </Col>
          </Row>
        </div>
      </div>

      <Row>
        <div className='mb-2'>
          <span
            style={{
              display: 'inline-block',
              marginRight: '4px',
              color: '#ff4d4f',
              fontSize: '14px',
              fontFamily: 'SimSun, sans-serif',
              lineHeight: 1,
            }}
          >
            *
          </span>
          <span>{t('production.form.stageTargetTitle')}</span>
          <Tooltip title={t('production.detail.stageTargetTitleDescription')}>
            <QuestionCircleOutlined className='ml-4px text-gray-500' />
          </Tooltip>
        </div>

        <Col span={24}>
          <Form.List
            name='stageTarget'
            rules={[
              {
                validator: (_, stageTargets) => {
                  if (!stageTargets || !stageTargets.filter(Boolean).length) {
                    return Promise.reject(new Error(t('production.form.stageTargetError')))
                  } else {
                    return Promise.resolve()
                  }
                },
              },
            ]}
          >
            {(fields, { add, remove }, { errors }) => {
              return (
                <>
                  {fields.map(({ key, name, ...restField }, index) => {
                    return (
                      <Form.Item key={key} noStyle>
                        <Form.Item
                          valuePropName='content'
                          label={
                            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                              <span>{t('production.form.stageTarget.title') + ` ${index + 1}`}</span>
                              {canDeleteStage && (
                                <MinusCircleOutlined className='text-red-500' onClick={() => remove(name)} />
                              )}
                            </div>
                          }
                          {...restField}
                          required
                          name={[name, 'value']}
                        >
                          <Editor
                            textMaxCount={ProductionFormInputMaxCount}
                            className={'editorBorder'}
                            disableAutoSaveStyle
                          />
                        </Form.Item>
                        <Form.Item
                          label={t('production.flowStatus.ResourceWaitForEstimate.form.predictWorkloadTitle')}
                          name={[name, 'predictWorkload']}
                          // tooltip={t('production.flowStatus.ResourceWaitForEstimate.form.predictWorkloadTooltip')}
                        >
                          <InputNumber
                            min={0}
                            precision={0}
                            disabled={!canEditPredictWorkload}
                            readOnly={!canEditPredictWorkload}
                          />
                        </Form.Item>
                        <Form.Item
                          label={t('production.form.stageTarget.date')}
                          required
                          {...restField}
                          name={[name, 'date']}
                        >
                          <DatePicker className='w-full' />
                        </Form.Item>
                        <Form.Item
                          valuePropName='content'
                          label={t('production.form.stageTarget.standard')}
                          required
                          {...restField}
                          name={[name, 'standard']}
                        >
                          <Editor
                            textMaxCount={ProductionFormInputMaxCount}
                            className={'editorBorder'}
                            disableAutoSaveStyle
                          />
                        </Form.Item>
                        <Form.Item
                          valuePropName='content'
                          label={t('production.form.stageTarget.content')}
                          required
                          {...restField}
                          name={[name, 'content']}
                        >
                          <Editor
                            textMaxCount={ProductionFormInputMaxCount}
                            className={'editorBorder'}
                            disableAutoSaveStyle
                          />
                        </Form.Item>
                      </Form.Item>
                    )
                  })}
                  {canAddStage && (
                    <Form.Item>
                      <Button icon={<PlusOutlined className='text-blue-500' />} type='dashed' onClick={() => add()}>
                        <span className='text-blue-500'>{t('production.form.stage.add.button')}</span>
                      </Button>
                      <Form.ErrorList errors={errors} />
                    </Form.Item>
                  )}
                </>
              )
            }}
          </Form.List>
        </Col>
      </Row>
    </>
  ) : null
}
